// ============================================================
// xFreeScan - Auth Context with OTP Flow
// ============================================================
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, dashboardApi } from '../lib/api';

interface User {
  id: string;
  email: string;
  nombre_empresa: string;
  movil: string;
  pais: string;
  contacto_nombre?: string;
  plan: string;
  api_token?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  register: (data: { email: string; nombre_empresa: string; movil: string; pais: string; contacto_nombre?: string }) => Promise<{ email: string }>;
  requestOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, code: string) => Promise<void>;
  resendOtp: (email: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('xfreescan_token');
    if (savedToken) {
      setToken(savedToken);
      loadUser();
    } else {
      setIsLoading(false);
    }
  }, []);

  const loadUser = async () => {
    try {
      const response = await dashboardApi.getUser();
      if (response.success && response.data) {
        setUser(response.data);
      } else {
        localStorage.removeItem('xfreescan_token');
        setToken(null);
      }
    } catch {
      localStorage.removeItem('xfreescan_token');
      setToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const register = useCallback(async (data: { email: string; nombre_empresa: string; movil: string; pais: string; contacto_nombre?: string }) => {
    const response = await authApi.register(data);
    return { email: response.data.email };
  }, []);

  const requestOtp = useCallback(async (email: string) => {
    await authApi.login({ email });
  }, []);

  const verifyOtp = useCallback(async (email: string, code: string) => {
    const response = await authApi.verifyOtp({ email, code });
    if (response.success && response.data) {
      localStorage.setItem('xfreescan_token', response.data.token);
      setToken(response.data.token);
      setUser(response.data.user);
    }
  }, []);

  const resendOtp = useCallback(async (email: string) => {
    await authApi.resendOtp({ email });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('xfreescan_token');
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, isLoading, isAuthenticated: !!user && !!token, register, requestOtp, verifyOtp, resendOtp, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
